import styled, { css, keyframes } from 'styled-components';

import { breakpoints } from 'theme';

const colors = {
  green: '#0f241f',
  greenMid: '#00b8a8',
  greenLight: '#e1f5ee',
  greenPale: '#f0faf6',
  text: '#111',
  textMuted: '#555',
  textFaint: '#999',
  border: '#e2e8e4',
  surface: '#f7f9fb',
  white: '#fff',
  amber: '#f5a623',
  amberPale: '#fff8ed',
  amberBorder: '#f5c842',
  amberText: '#a06a0a',
  amberStrong: '#7a5c00',
  red: '#e24b4a',
  redPale: '#fff0f0',
  redBorder: '#fca5a5',
  redText: '#7f1d1d',
};

export const PageRoot = styled.div`
  background: ${colors.surface};
  color: ${colors.text};
  width: 100%;
  min-height: 100%;
`;

export const PageWrap = styled.div`
  max-width: 660px;
  margin: 0 auto;
  padding: 36px 24px 80px;

  @media ${breakpoints.downSmPlus} {
    padding: 24px 16px 60px;
  }
`;

/* Step header */
export const StepHeader = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

export const StepTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0 0 6px;
`;

export const StepDesc = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${colors.textMuted};
  line-height: 1.6;
`;

/* Account card */
export const AccountCard = styled.div`
  background: ${colors.white};
  border: 1.5px solid ${colors.border};
  border-radius: 20px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 12px;
  flex-wrap: wrap;
  text-align: left;
`;

export const AccountLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const AccountAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${colors.green};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const AccountName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text};
  word-break: break-word;
`;

export const AccountMeta = styled.div`
  font-size: 12px;
  color: ${colors.textFaint};
  margin-top: 2px;
`;

export const PlanPill = styled.span<{
  $tone: 'trial' | 'pro' | 'premium' | 'enterprise';
}>`
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 40px;
  white-space: nowrap;
  ${({ $tone }) => {
    if ($tone === 'trial') {
      return css`
        background: ${colors.amberPale};
        color: ${colors.amberText};
        border: 1.5px solid ${colors.amberBorder};
      `;
    }
    if ($tone === 'pro') {
      return css`
        background: ${colors.surface};
        color: ${colors.textMuted};
        border: 1.5px solid ${colors.border};
      `;
    }
    if ($tone === 'premium') {
      return css`
        background: ${colors.greenLight};
        color: #085041;
        border: 1.5px solid ${colors.greenMid};
      `;
    }
    return css`
      background: ${colors.green};
      color: #fff;
    `;
  }}
`;

/* Reason cards */
export const ReasonGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
  margin-bottom: 24px;
`;

export const ReasonCard = styled.button<{ $selected?: boolean }>`
  appearance: none;
  text-align: left;
  width: 100%;
  background: ${({ $selected }) =>
    $selected ? colors.greenPale : colors.white};
  border: 1.5px solid
    ${({ $selected }) => ($selected ? colors.green : colors.border)};
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${colors.green};
    background: ${colors.greenPale};
  }
`;

export const ReasonDot = styled.span<{ $selected?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    ${({ $selected }) => ($selected ? colors.green : colors.border)};
  background: ${({ $selected }) => ($selected ? colors.green : 'transparent')};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.15s;

  &::after {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    opacity: ${({ $selected }) => ($selected ? 1 : 0)};
    transition: opacity 0.15s;
  }
`;

export const ReasonText = styled.div`
  font-size: 14px;
  color: ${colors.text};
  font-weight: 500;
`;

export const ReasonSub = styled.div`
  font-size: 12px;
  color: ${colors.textFaint};
  margin-top: 2px;
`;

/* Followup */
export const FollowUpWrap = styled.div`
  margin-bottom: 24px;
`;

export const FollowUpLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text};
  margin-bottom: 8px;
`;

export const FollowUpTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid ${colors.border};
  border-radius: 14px;
  font-size: 13px;
  color: ${colors.text};
  background: ${colors.white};
  resize: vertical;
  min-height: 80px;
  outline: none;
  transition: border-color 0.15s;
  line-height: 1.5;

  &:focus {
    border-color: ${colors.green};
  }
`;

/* Retention cards */
export const RetentionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: 1fr;
  }
`;

export const RetentionCard = styled.div<{ $featured?: boolean }>`
  background: ${({ $featured }) =>
    $featured ? colors.greenPale : colors.white};
  border: 1.5px solid
    ${({ $featured }) => ($featured ? colors.green : colors.border)};
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  }
`;

export const RetentionIcon = styled.div`
  font-size: 20px;
  margin-bottom: 8px;
`;

export const RetentionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: 4px;
`;

export const RetentionDesc = styled.div`
  font-size: 12px;
  color: ${colors.textMuted};
  line-height: 1.5;
  flex: 1;
`;

export const RetentionCta = styled.button<{ $primary?: boolean }>`
  width: 100%;
  margin-top: 12px;
  padding: 9px;
  border-radius: 40px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: ${({ $primary }) =>
    $primary ? 'none' : `1.5px solid ${colors.border}`};
  background: ${({ $primary }) => ($primary ? colors.green : 'transparent')};
  color: ${({ $primary }) => ($primary ? '#fff' : colors.textMuted)};

  &:hover {
    ${({ $primary }) =>
      $primary
        ? 'background: #0a1a14;'
        : `border-color: ${colors.green}; color: ${colors.green};`}
  }
`;

/* Offer / pause / enterprise box */
export const OfferBox = styled.div`
  background: ${colors.amberPale};
  border: 1.5px solid ${colors.amberBorder};
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  text-align: left;
`;

export const OfferIcon = styled.div`
  font-size: 22px;
  flex-shrink: 0;
`;

export const OfferTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.amberStrong};
  margin-bottom: 4px;
`;

export const OfferDesc = styled.div`
  font-size: 12px;
  color: ${colors.amberText};
  line-height: 1.5;
  margin-bottom: 10px;
`;

export const OfferCta = styled.button`
  padding: 8px 20px;
  border-radius: 40px;
  border: none;
  background: ${colors.amber};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #d4881a;
  }
`;

export const EntOfferBox = styled.div`
  background: ${colors.greenPale};
  border: 1.5px solid ${colors.greenLight};
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
  text-align: left;
`;

export const EntOfferTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.green};
  margin-bottom: 4px;
`;

export const EntOfferDesc = styled.div`
  font-size: 12px;
  color: #085041;
  line-height: 1.5;
  margin-bottom: 10px;
`;

export const EntOfferCta = styled.button`
  padding: 8px 20px;
  border-radius: 40px;
  border: none;
  background: ${colors.green};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #0a1a14;
  }
`;

/* Loss / confirm summary */
export const LossBox = styled.div`
  background: ${colors.redPale};
  border: 1px solid ${colors.redBorder};
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 24px;
`;

export const LossHeading = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.redText};
  margin: 0 0 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const LossList = styled.ul`
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  margin: 0;
  padding: 0;

  @media ${breakpoints.downSmPlus} {
    grid-template-columns: 1fr;
  }
`;

export const LossItem = styled.li`
  font-size: 12px;
  color: ${colors.redText};
  display: flex;
  align-items: flex-start;
  gap: 7px;
  line-height: 1.4;

  span {
    flex-shrink: 0;
    font-size: 12px;
    margin-top: 1px;
  }
`;

export const ConfirmSummary = styled.div`
  background: ${colors.white};
  border: 1.5px solid ${colors.border};
  border-radius: 20px;
  padding: 20px 24px;
  margin-bottom: 20px;
`;

export const ConfirmRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 9px 0;
  border-bottom: 1px solid ${colors.border};
  font-size: 13px;
  gap: 16px;

  &:last-child {
    border-bottom: none;
  }
`;

export const ConfirmLabel = styled.span`
  color: ${colors.textFaint};
  flex-shrink: 0;
  text-align: left;
`;

export const ConfirmValue = styled.span<{ $tone?: 'red' | 'green' }>`
  font-weight: 500;
  margin-left: auto;
  text-align: right;
  color: ${({ $tone }) => {
    if ($tone === 'red') return colors.red;
    if ($tone === 'green') return colors.greenMid;
    return colors.text;
  }};
`;

export const ConfirmWarn = styled.div`
  background: ${colors.redPale};
  border: 1px solid ${colors.redBorder};
  border-radius: 14px;
  padding: 14px 16px;
  font-size: 13px;
  text-align: left;
  color: ${colors.redText};
  line-height: 1.6;
  margin-bottom: 20px;

  strong {
    font-weight: 600;
    display: block;
    margin-bottom: 3px;
  }
`;

export const InfoBox = styled.div`
  background: ${colors.greenPale};
  border: 1px solid ${colors.greenLight};
  border-radius: 14px;
  padding: 12px 14px;
  text-align: left;
  font-size: 13px;
  color: #085041;
  line-height: 1.55;
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    font-weight: 700;
  }
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  background: ${colors.white};
  border: 1.5px solid ${colors.border};
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 24px;
  cursor: pointer;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${colors.redBorder};
  }

  input[type='checkbox'] {
    margin-top: 2px;
    flex-shrink: 0;
    accent-color: ${colors.red};
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  span {
    font-size: 13px;
    color: ${colors.textMuted};
    line-height: 1.55;

    strong {
      color: ${colors.text};
      font-weight: 600;
    }
  }
`;

/* Buttons */
export const BtnPrimary = styled.button`
  padding: 12px 28px;
  border-radius: 40px;
  background: ${colors.green};
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;

  &:hover {
    background: #0a1a14;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const BtnSecondary = styled.button`
  padding: 12px 28px;
  border-radius: 40px;
  background: transparent;
  border: 1.5px solid ${colors.border};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.textMuted};
  cursor: pointer;

  &:hover {
    border-color: ${colors.green};
    color: ${colors.green};
  }
`;

export const BtnDanger = styled.button`
  padding: 12px 28px;
  border-radius: 40px;
  background: transparent;
  border: 1.5px solid ${colors.redBorder};
  font-size: 14px;
  font-weight: 600;
  color: ${colors.red};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${colors.redPale};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 8px;

  > :last-child {
    margin-left: auto;
  }
`;

export const BackLink = styled.button`
  appearance: none;
  background: none;
  border: none;
  font-size: 13px;
  color: ${colors.textFaint};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;

  &:hover {
    color: ${colors.green};
  }
`;

export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${colors.border};
  }

  span {
    font-size: 11px;
    color: ${colors.textFaint};
  }
`;

/* Done */
export const SuccessWrap = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

export const SuccessIconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${colors.greenLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
  font-size: 28px;
`;

export const SuccessTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text};
  margin: 0 0 8px;
`;

export const SuccessSub = styled.p`
  font-size: 14px;
  color: ${colors.textMuted};
  line-height: 1.65;
  max-width: 480px;
  margin: 0 auto 28px;

  strong {
    color: ${colors.text};
    font-weight: 600;
  }
`;

export const SuccessCard = styled.div<{ $green?: boolean }>`
  background: ${({ $green }) => ($green ? colors.greenPale : colors.white)};
  border: 1.5px solid
    ${({ $green }) => ($green ? colors.greenLight : colors.border)};
  border-radius: 14px;
  padding: 16px 20px;
  text-align: left;
  margin-bottom: 14px;

  h4 {
    font-size: 13px;
    font-weight: 600;
    color: ${({ $green }) => ($green ? colors.green : colors.text)};
    margin: 0 0 10px;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    font-size: 13px;
    color: ${colors.textMuted};
    padding: 4px 0;
    display: flex;
    gap: 9px;
    align-items: flex-start;
    line-height: 1.4;
  }

  li span {
    color: ${colors.greenMid};
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const SuccessActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 4px;
`;

export const UndoBanner = styled.div`
  background: ${colors.greenPale};
  border: 1.5px solid ${colors.greenMid};
  border-radius: 14px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
  text-align: left;

  > :last-child {
    margin-left: auto;
  }
`;

export const UndoText = styled.div`
  font-size: 13px;
  color: ${colors.green};
  font-weight: 500;
`;

export const UndoSub = styled.div`
  font-size: 12px;
  color: ${colors.greenMid};
  margin-top: 2px;
`;

export const UndoBtn = styled.button`
  padding: 9px 22px;
  border-radius: 40px;
  background: ${colors.green};
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #0a1a14;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s linear infinite;
`;

export const ErrorNote = styled.div`
  background: ${colors.redPale};
  border: 1px solid ${colors.redBorder};
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 13px;
  color: ${colors.redText};
  line-height: 1.5;
  margin-bottom: 16px;
`;
